import React from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  alpha,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { SortableScheduleList } from "../TaxComputationConsole/SchedulesList/BaseSchedulesMenu";
import IconButtonWithTooltip from "../utils/IconButtonWithTooltip";
import {
  getSchedulePanelDisplayConfig,
  shouldUseLargeSchedulePanel,
} from "../../utils/responsiveLayoutSettings";

// ==================== 可复用组件：SchedulePanel ====================

const SchedulePanel = ({
  title,
  icon: Icon,
  schedules,
  filteredSchedules,
  searchQuery,
  setSearchQuery,
  onAddSchedule,
  onOpenSchedule,
  moveSchedule,
  reorderSchedule,
  DndItemComponent,
  canEditSchedule,
  isFetching,
  emptyStateStyles,
  panelKey,
}) => {
  const theme = useTheme();
  const [useLargeLayout, setUseLargeLayout] = React.useState(() =>
    shouldUseLargeSchedulePanel()
  );

  React.useEffect(() => {
    const updateLayoutMode = () =>
      setUseLargeLayout(shouldUseLargeSchedulePanel());

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    window.visualViewport?.addEventListener("resize", updateLayoutMode);

    return () => {
      window.removeEventListener("resize", updateLayoutMode);
      window.visualViewport?.removeEventListener("resize", updateLayoutMode);
    };
  }, []);

  // 面板配置（根据类型定制）
  const config = {
    pl: {
      gradient: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
      iconBg: alpha(theme.palette.primary.main, 0.08),
      accentColor: theme.palette.primary.main,
    },
    ca: {
      gradient: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.06)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
      iconBg: alpha(theme.palette.success.main, 0.08),
      accentColor: theme.palette.success.main,
    },
  };

  const panelConfig = config[panelKey] || config.pl;
  const displayConfig = getSchedulePanelDisplayConfig(useLargeLayout);
  const isSearchMode = Boolean(searchQuery?.trim());

  return (
    <Paper
      elevation={0}
      sx={{
        height: displayConfig.height,
        minHeight: displayConfig.minHeight,
        display: "flex",
        flexDirection: "column",
        borderRadius: displayConfig.borderRadius,
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
        overflow: "hidden",
        transition: "all 0.25s ease-out",
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.98),
        "&:hover": {
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.4),
          transform: "translateY(-1px)",
        },
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: -1,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${panelConfig.accentColor}, ${alpha(
            panelConfig.accentColor,
            0.25
          )}, transparent)`,
        },
      }}
    >
      <Box
        sx={{
          px: displayConfig.header.px,
          py: displayConfig.header.py,
          display: "flex",
          flexDirection: {
            xs: "column",
            sm: useLargeLayout ? "column" : "row",
            lg: "row",
          },
          alignItems: {
            xs: "stretch",
            sm: useLargeLayout ? "stretch" : "center",
            lg: "center",
          },
          justifyContent: "space-between",
          gap: displayConfig.header.gap,
          borderBottom: "1px solid",
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.2),
          background: "rgba(255, 255, 255, 0.96)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.03)",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -1,
            left: 0,
            right: 0,
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${panelConfig.accentColor} 30%, ${alpha(
              panelConfig.accentColor,
              0.1
            )} 70%, transparent)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: displayConfig.header.gap,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: displayConfig.header.iconContainerSize,
              height: displayConfig.header.iconContainerSize,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: displayConfig.header.iconRadius,
              bgcolor: panelConfig.iconBg,
              transition: "all 0.3s ease",
              flexShrink: 0,
              "& svg": {
                fontSize: displayConfig.header.iconSize,
                color: panelConfig.accentColor,
              },
              "&:hover": {
                transform: "scale(1.08) rotate(5deg)",
                boxShadow: `0 4px 16px ${alpha(panelConfig.accentColor, 0.2)}`,
              },
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography
              variant={displayConfig.header.titleVariant}
              fontWeight={700}
              sx={{
                background: `linear-gradient(45deg, ${panelConfig.accentColor} 0%, ${alpha(
                  panelConfig.accentColor,
                  0.6
                )} 100%)`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.2,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: isFetching
                    ? "warning.main"
                    : filteredSchedules?.length > 0
                      ? "success.main"
                      : "grey.400",
                  animation: isFetching
                    ? "pulse 1.5s ease-in-out infinite"
                    : "none",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                  },
                }}
              />
              {isFetching
                ? "Loading..."
                : `${filteredSchedules?.length || 0} schedules`}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: displayConfig.header.controlsGap,
            flexWrap: "wrap",
            minWidth: 0,
          }}
        >
          <TextField
            size='small'
            placeholder={`Search Schedules...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon
                    color='action'
                    fontSize='small'
                    sx={{
                      opacity: searchQuery ? 1 : 0.5,
                      transition: "opacity 0.2s ease",
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position='end'>
                  <IconButton
                    size='small'
                    onClick={() => setSearchQuery("")}
                    sx={{ p: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        bgcolor: "grey.300",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                        fontSize: "10px",
                        fontWeight: 700,
                      }}
                    >
                      ✕
                    </Box>
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              flex: 1,
              minWidth: displayConfig.header.searchMinWidth,
              maxWidth: displayConfig.header.searchMaxWidth,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5,
                bgcolor: alpha(theme.palette.background.paper, 0.6),
                transition: "all 0.2s ease",
                "&:hover fieldset": {
                  borderColor: panelConfig.accentColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: panelConfig.accentColor,
                  borderWidth: 1.5,
                  boxShadow: `0 0 0 4px ${alpha(panelConfig.accentColor, 0.12)}`,
                },
                "& .MuiInputBase-input": {
                  py: displayConfig.header.searchInputPaddingY,
                  px: displayConfig.header.searchInputPaddingX,
                },
              },
            }}
          />
          {canEditSchedule && (
            <IconButtonWithTooltip
              icon={AddIcon}
              tooltipTitle={`Create ${title}`}
              onClick={onAddSchedule}
              color='primary'
              size='medium'
              sx={{
                width: displayConfig.addButtonSize,
                height: displayConfig.addButtonSize,
                borderRadius: 2.5,
                bgcolor: panelConfig.iconBg,
                color: panelConfig.accentColor,
                transition: "all 0.2s ease",
                "&:hover": {
                  bgcolor: panelConfig.accentColor,
                  color: theme.palette.primary.contrastText,
                  transform: "scale(1.05) rotate(90deg)",
                  boxShadow: `0 4px 16px ${alpha(panelConfig.accentColor, 0.3)}`,
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          p: displayConfig.contentPadding,
        }}
      >
        {isFetching ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: displayConfig.skeleton.gap,
              p: displayConfig.skeleton.padding,
            }}
          >
            {[1, 2, 3, 4].map((key) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: displayConfig.skeleton.itemGap,
                  p: displayConfig.skeleton.itemPadding,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  animation: "skeletonPulse 1.8s ease-in-out infinite",
                  animationDelay: `${key * 0.1}s`, // 错开动画，更自然
                  "@keyframes skeletonPulse": {
                    "0%, 100%": { opacity: 0.6 },
                    "50%": { opacity: 0.9 },
                  },
                }}
              >
                {/* 拖拽手柄占位 */}
                <Box
                  sx={{
                    width: displayConfig.skeleton.sideBoxSize,
                    height: displayConfig.skeleton.sideBoxSize,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.action.hover, 0.6),
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.4,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: 14,
                      height: 2,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.text.secondary, 0.3),
                    }}
                  />
                  <Box
                    sx={{
                      width: 14,
                      height: 2,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.text.secondary, 0.3),
                    }}
                  />
                </Box>

                {/* 内容区域占位 */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  {/* 标题行 */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        height: 14,
                        width: "60%",
                        maxWidth: 180,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.text.primary, 0.15),
                      }}
                    />
                    <Box
                      sx={{
                        height: 10,
                        width: 32,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.action.hover, 0.5),
                      }}
                    />
                  </Box>
                  {/* 副标题行 */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.text.secondary, 0.2),
                      }}
                    />
                    <Box
                      sx={{
                        height: 12,
                        width: "40%",
                        maxWidth: 120,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.text.secondary, 0.12),
                      }}
                    />
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: alpha(theme.palette.text.secondary, 0.1),
                      }}
                    />
                    <Box
                      sx={{
                        height: 10,
                        width: "30%",
                        maxWidth: 90,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.text.secondary, 0.1),
                      }}
                    />
                  </Box>
                </Box>

                {/* 右侧箭头占位 */}
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.action.hover, 0.4),
                    flexShrink: 0,
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : !filteredSchedules || filteredSchedules.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: displayConfig.emptyState.padding,
              textAlign: "center",
              bgcolor: alpha(theme.palette.background.paper, 0.5),
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
              ...emptyStateStyles,
            }}
          >
            <Box
              sx={{
                width: displayConfig.emptyState.iconContainerSize,
                height: displayConfig.emptyState.iconContainerSize,
                borderRadius: "50%",
                bgcolor: alpha(panelConfig.accentColor, 0.08),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.1) rotate(5deg)",
                  bgcolor: alpha(panelConfig.accentColor, 0.12),
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: displayConfig.emptyState.iconSize,
                  color: alpha(panelConfig.accentColor, 0.5),
                }}
              />
            </Box>
            <Typography
              variant='body1'
              fontWeight={600}
              color='text.primary'
              sx={{ mb: 0.5 }}
            >
              {searchQuery
                ? "No matching schedules found"
                : "No schedules available"}
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mb: 2, maxWidth: displayConfig.emptyState.textMaxWidth }}
            >
              {searchQuery
                ? "Try adjusting your search terms"
                : `Create your first ${title.toLowerCase()} to get started`}
            </Typography>
            {!searchQuery && canEditSchedule && (
              <Button
                variant='contained'
                startIcon={<AddIcon />}
                onClick={onAddSchedule}
                size='small'
                sx={{
                  borderRadius: 2.5,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  bgcolor: panelConfig.accentColor,
                  "&:hover": {
                    bgcolor: panelConfig.accentColor,
                    transform: "translateY(-2px)",
                    boxShadow: `0 4px 16px ${alpha(panelConfig.accentColor, 0.3)}`,
                  },
                }}
              >
                Create Schedule
              </Button>
            )}
          </Box>
        ) : (
          <SortableScheduleList
            items={filteredSchedules}
            allItems={schedules}
            moveSchedule={moveSchedule}
            reorderSchedule={reorderSchedule}
            DndItemComponent={DndItemComponent}
            onOpenSchedule={onOpenSchedule}
            canMoveSchedule={canEditSchedule && !isSearchMode}
            showFullWidth={true}
            hideDragHandle={isSearchMode}
            listSx={{
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: displayConfig.list.gap,
              px: displayConfig.list.px,
              py: displayConfig.list.py,
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.action.hover, 0.3),
                borderRadius: 3,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.action.disabled, 0.5),
                borderRadius: 3,
                transition: "background-color 0.2s ease",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: alpha(theme.palette.action.disabled, 0.8),
              },
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default SchedulePanel;
